import { useMemo } from 'react';
import { View, ViewProps } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { useCurrentOverviewDate } from '~/calendar/hooks/useCurrentOverviewDate';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { MealUtils, DEFAULT_WATER_TARGET_ML } from '@fytness/shared';
import type { Color } from '@/lib/colors';
import { useQuery } from '@apollo/client/react';
import {
  CurrentUserDocument,
  type Meal,
  MealsByIdsDocument,
} from '~/graphql/generated/graphql';
import { MealSummaryData } from '@/lib/local-api/meal';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { useColor } from '~/global/hooks/useColor';
import Svg, { Circle } from 'react-native-svg';

export interface MealsOverviewSectionProps extends ViewProps {}

const CALORIE_RING_SIZE = 100;
const CALORIE_RING_STROKE = 10;
const CALORIE_RING_RADIUS = (CALORIE_RING_SIZE - CALORIE_RING_STROKE) / 2;
const CALORIE_RING_CIRCUMFERENCE = 2 * Math.PI * CALORIE_RING_RADIUS;

const MACRO_RING_SIZE = 40;
const MACRO_RING_STROKE = 4;
const MACRO_RING_RADIUS = (MACRO_RING_SIZE - MACRO_RING_STROKE) / 2;
const MACRO_RING_CIRCUMFERENCE = 2 * Math.PI * MACRO_RING_RADIUS;

// TODO: replace with user-configurable daily macro targets
const DEFAULT_CALORIE_TARGET = 2000;
const DEFAULT_MACRO_TARGETS = { carbs: 250, proteins: 120, fats: 65 };

/**
 * Hydration is shown in litres: `0.25 L`, `1.5 L`, `2 L`. Two decimals keeps small
 * amounts honest against the ring fill (250 ml really is 0.25 L, not 0.3 L), and
 * `toLocaleString` both trims trailing zeros and uses the locale's decimal separator
 * (Slovak renders `0,25`). The summed value keeps full precision; this rounds only
 * for display.
 */
function formatLitres(ml: number, locale: string): string {
  const litres = (ml / 1000).toLocaleString(locale, {
    maximumFractionDigits: 2,
  });
  return `${litres} L`;
}

function CalorieRing({ progress }: { progress: number }) {
  const accentColor = useColor('accent');
  const borderColor = useColor('border');

  const clampedProgress = Math.min(progress, 1);
  const strokeDashoffset = CALORIE_RING_CIRCUMFERENCE * (1 - clampedProgress);

  return (
    <View
      className="relative items-center justify-center"
      style={{ width: CALORIE_RING_SIZE, height: CALORIE_RING_SIZE }}
    >
      <Svg
        width={CALORIE_RING_SIZE}
        height={CALORIE_RING_SIZE}
        style={{ position: 'absolute' }}
      >
        <Circle
          cx={CALORIE_RING_SIZE / 2}
          cy={CALORIE_RING_SIZE / 2}
          r={CALORIE_RING_RADIUS}
          stroke={borderColor}
          strokeWidth={CALORIE_RING_STROKE}
          fill="none"
        />
        <Circle
          cx={CALORIE_RING_SIZE / 2}
          cy={CALORIE_RING_SIZE / 2}
          r={CALORIE_RING_RADIUS}
          stroke={accentColor}
          strokeWidth={CALORIE_RING_STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={CALORIE_RING_CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          rotation={-90}
          origin={`${CALORIE_RING_SIZE / 2}, ${CALORIE_RING_SIZE / 2}`}
        />
      </Svg>
    </View>
  );
}

function MacroRing({
  progress,
  color = 'accent',
}: {
  progress: number;
  color?: Color;
}) {
  const accentColor = useColor(color);
  const borderColor = useColor('border');

  const clampedProgress = Math.min(progress, 1);
  const strokeDashoffset = MACRO_RING_CIRCUMFERENCE * (1 - clampedProgress);

  return (
    <View
      className="relative items-center justify-center"
      style={{ width: MACRO_RING_SIZE, height: MACRO_RING_SIZE }}
    >
      <Svg
        width={MACRO_RING_SIZE}
        height={MACRO_RING_SIZE}
        style={{ position: 'absolute' }}
      >
        <Circle
          cx={MACRO_RING_SIZE / 2}
          cy={MACRO_RING_SIZE / 2}
          r={MACRO_RING_RADIUS}
          stroke={borderColor}
          strokeWidth={MACRO_RING_STROKE}
          fill="none"
        />
        <Circle
          cx={MACRO_RING_SIZE / 2}
          cy={MACRO_RING_SIZE / 2}
          r={MACRO_RING_RADIUS}
          stroke={accentColor}
          strokeWidth={MACRO_RING_STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={MACRO_RING_CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          rotation={-90}
          origin={`${MACRO_RING_SIZE / 2}, ${MACRO_RING_SIZE / 2}`}
        />
      </Svg>
    </View>
  );
}

export const MealsOverviewSection = ({
  className,
  ...props
}: MealsOverviewSectionProps) => {
  const { t, i18n } = useTranslation('meal');
  const selectedDate = useCurrentOverviewDate();

  const { data: userData } = useQuery(CurrentUserDocument);
  const calorieTarget =
    userData?.currentUser?.calorieGoal ?? DEFAULT_CALORIE_TARGET;

  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forSingleDay(selectedDate);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<{
    id: string;
    mealId: string;
    mealType: string | null;
    consumedAmount: number | null;
    consumedAt: string;
  }>(db.MealLog.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  const mealLogs = useMemo(
    () =>
      ConsumedDateRange.filterToDays(rawMealLogs, selectedDate, selectedDate),
    [rawMealLogs, selectedDate],
  );

  const mealIds = useMemo(
    () =>
      Array.from(
        new Set((mealLogs ?? []).map((l) => l.mealId).filter(Boolean)),
      ),
    [mealLogs],
  );
  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });
  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => map.set(m.id, m as Meal));
    return map;
  }, [mealsData]);

  const summary: MealSummaryData = useMemo(() => {
    if (!mealLogs || mealLogs.length === 0)
      return {
        kcal: 0,
        macros: { proteins: 0, carbs: 0, fats: 0, fiber: 0 },
        mealCount: 0,
      };
    const totals = mealLogs.reduce(
      (acc, log) => {
        const meal = mealsById.get(log.mealId);
        if (!meal) return acc;
        const adj = MealUtils.adjustedNutrition(meal, log.consumedAmount ?? 0);
        acc.kcal += adj.calories ?? 0;
        acc.proteins += adj.protein ?? 0;
        acc.carbs += adj.carbs ?? 0;
        acc.fats += adj.fats ?? 0;
        acc.fiber += adj.fiber ?? 0;
        return acc;
      },
      { kcal: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0 },
    );
    return {
      kcal: totals.kcal,
      macros: {
        proteins: totals.proteins,
        carbs: totals.carbs,
        fats: totals.fats,
        fiber: totals.fiber,
      },
      mealCount: mealLogs.length,
    };
  }, [mealLogs, mealsById]);

  // Hydration total (ml): sum each log's water content, exactly like the macro
  // reduce above. Full Decimal precision is kept here; rounding happens at display.
  const waterMl = useMemo(() => {
    if (!mealLogs || mealLogs.length === 0) return 0;
    return mealLogs.reduce((sum, log) => {
      const meal = mealsById.get(log.mealId);
      if (!meal) return sum;
      return (
        sum +
        (MealUtils.adjustedNutrition(meal, log.consumedAmount ?? 0).water ?? 0)
      );
    }, 0);
  }, [mealLogs, mealsById]);

  const eaten = Math.round(summary.kcal);
  const remaining = Math.max(0, calorieTarget - eaten);
  const calorieProgress = eaten / calorieTarget;
  const waterProgress = waterMl / DEFAULT_WATER_TARGET_ML;

  return (
    <View className={className} {...props}>
      <View className="mb-3 flex flex-row items-center gap-3">
        <View className="h-0.5 w-6 rounded-sm bg-accent" />
        <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
          {t('nutrition.title')}
        </Text>
      </View>

      {/* Calorie Ring Card */}
      <View className="flex flex-row items-center rounded-xl bg-surface p-4">
        <View className="flex-1 items-center">
          <View className="relative items-center justify-center">
            <CalorieRing progress={calorieProgress} />
            <View className="absolute items-center gap-0.5">
              <Text
                className="font-sora-bold text-text-primary"
                style={{ fontSize: 20, letterSpacing: -1 }}
              >
                {eaten.toLocaleString()}
              </Text>
              <Text
                className="font-inter-medium text-text-muted"
                style={{ fontSize: 11 }}
              >
                {t('nutrition.eaten')}
              </Text>
            </View>
          </View>
        </View>

        <View className="flex-1 items-center gap-0.5">
          <Text
            className="font-sora-bold text-accent"
            style={{ fontSize: 18, letterSpacing: -0.5 }}
          >
            {remaining.toLocaleString()}
          </Text>
          <Text
            className="font-inter-medium text-text-secondary"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.remaining')}
          </Text>
        </View>
        <View className="flex-1 items-center gap-0.5">
          <Text
            className="font-sora-bold text-text-primary"
            style={{ fontSize: 18, letterSpacing: -0.5 }}
          >
            {calorieTarget.toLocaleString()}
          </Text>
          <Text
            className="font-inter-medium text-text-secondary"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.dailyTarget')}
          </Text>
        </View>
      </View>

      {/* Macros Card */}
      <View className="mt-3 flex flex-row justify-between rounded-xl bg-surface p-3">
        <View className="flex-1 items-center gap-1.5">
          <MacroRing
            progress={summary.macros.carbs / DEFAULT_MACRO_TARGETS.carbs}
          />
          <Text className="font-sora-bold text-sm text-text-primary">
            {Math.round(summary.macros.carbs)}g
          </Text>
          <Text
            className="font-inter-medium text-text-muted"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.carbs.longLabel')}
          </Text>
        </View>
        <View className="flex-1 items-center gap-1.5">
          <MacroRing
            progress={summary.macros.proteins / DEFAULT_MACRO_TARGETS.proteins}
          />
          <Text className="font-sora-bold text-sm text-text-primary">
            {Math.round(summary.macros.proteins)}g
          </Text>
          <Text
            className="font-inter-medium text-text-muted"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.protein.longLabel')}
          </Text>
        </View>
        <View className="flex-1 items-center gap-1.5">
          <MacroRing
            progress={summary.macros.fats / DEFAULT_MACRO_TARGETS.fats}
          />
          <Text className="font-sora-bold text-sm text-text-primary">
            {Math.round(summary.macros.fats)}g
          </Text>
          <Text
            className="font-inter-medium text-text-muted"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.fats.longLabel')}
          </Text>
        </View>
        <View className="flex-1 items-center gap-1.5" testID="water-ring">
          <MacroRing progress={waterProgress} color="macro-water" />
          <Text
            className="font-sora-bold text-sm text-text-primary"
            testID="water-ring-value"
          >
            {t('nutrition.waterProgress', {
              current: formatLitres(waterMl, i18n.language),
              target: formatLitres(DEFAULT_WATER_TARGET_ML, i18n.language),
            })}
          </Text>
          <Text
            className="font-inter-medium text-text-muted"
            style={{ fontSize: 10 }}
          >
            {t('nutrition.water')}
          </Text>
        </View>
      </View>
    </View>
  );
};
